import React, { useState, useRef, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Link2, Loader2, RefreshCw, Search, FileText, Clock, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtD, fmtDT } from "@/lib/semanas";
import { ExportExcelButton } from "@/components/ExportExcelButton";
import { CneNotificacion } from "@/components/control/CneNotificacion";

const card = "bg-white rounded-2xl border border-slate-200/70 shadow-sm";
const n5 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`);
const folio = (c) => (c || "").replace(/^PM-DC-SEPIC-GPDC-SPPCI-/, "");
const pubLabel = (p) => `Circular ${folio(p.circular)} · ${fmtD(p.vigencia_inicio)} – ${fmtD(p.vigencia_fin)}${p.sustituida_por ? " · cancelada" : ""}`;

const usePemex = () => useQuery({ queryKey: ["pemex", "publicaciones"], queryFn: async () => (await api.get("/control-semanal/pemex/publicaciones")).data });

function PemexActions({ fuente, documento }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [url, setUrl] = useState("");
  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      const { data } = await fn();
      toast.success(`${data.circulares} circulares procesadas · ${data.nuevas} nuevas · ${data.actualizadas} actualizadas.`);
      qc.invalidateQueries({ queryKey: ["pemex"] });
      setUrl("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo importar.");
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const onFile = (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    run("file", () => api.post("/control-semanal/pemex/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } }));
  };
  const btn = "h-10 px-4 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 border border-slate-200 text-slate-700 hover:bg-slate-50";
  return (
    <div className={`${card} p-4 flex flex-wrap items-center gap-3`} data-testid="pemex-actions">
      <button onClick={() => run("sync", () => api.post("/control-semanal/pemex/sync"))} disabled={!!busy} data-testid="pemex-sync-btn" className="h-10 px-4 rounded-xl text-sm font-semibold flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
        {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Consultar fuente oficial
      </button>
      <input ref={fileRef} type="file" accept=".pdf" className="hidden" data-testid="pemex-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
      <button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="pemex-import-file-btn" className={btn}>
        {busy === "file" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Importar PDF
      </button>
      <div className="flex flex-wrap items-center gap-2 basis-full sm:basis-auto flex-1 min-w-0 sm:min-w-[280px]">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.pemex.com/…/Precios de Comercialización de GLP.pdf" data-testid="pemex-url-input"
          className="h-10 w-full min-w-0 sm:w-auto sm:flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-200" />
        <button onClick={() => run("url", () => api.post("/control-semanal/pemex/import-url", { url }))} disabled={!!busy || !url.trim()} data-testid="pemex-import-url-btn" className={btn}>
          {busy === "url" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}Importar enlace
        </button>
      </div>
      <FuenteLinks fuente={fuente} documento={documento} />
      <CneNotificacion tipo="pemex" />
    </div>
  );
}

function FuenteLinks({ fuente, documento }) {
  const a = "inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:underline";
  return (
    <div className="flex flex-wrap items-center gap-4">
      {fuente && <a href={fuente} target="_blank" rel="noreferrer" className={a} data-testid="pemex-fuente-link"><ExternalLink className="w-4 h-4" />Fuente oficial</a>}
      {documento && <a href={documento} target="_blank" rel="noreferrer" className={a} data-testid="pemex-descargar-link"><Download className="w-4 h-4" />Descargar PDF</a>}
    </div>
  );
}

function TablaPrecios({ rows, loading }) {
  const th = "px-4 py-2.5 text-right";
  return (
    <div className={`${card} overflow-hidden`}>
      <div className="max-h-[560px] overflow-auto">
        <table className="w-full text-sm min-w-[860px]" data-testid="pemex-precios-table">
          <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-4 py-2.5">Punto de entrega</th><th className={th}>Precio sin condiciones</th><th className={th}>Ajuste por calidad</th><th className={th}>Precio sin IVA</th><th className={th}>IVA</th><th className={th}>Precio con IVA</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="w-5 h-5 text-red-600 animate-spin inline" /></td></tr> :
              rows.map((r) => (
                <tr key={r.punto} className="border-t border-slate-100 hover:bg-red-50/30" data-testid={`pemex-row-${r.punto}`}>
                  <td className="px-4 py-2 font-semibold text-slate-800">{r.punto}{r.nota_iva_8 && <span className="ml-1.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" title="IVA del 8% en la zona fronteriza norte">IVA 8%</span>}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{n5(r.precio_base)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{n5(r.ajuste)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-slate-900">{n5(r.precio_sin_iva)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{n5(r.iva)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-800">{n5(r.precio_con_iva)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100" data-testid="pemex-count">{rows.length} puntos de entrega · precios en pesos por kilogramo</div>
    </div>
  );
}

export function PemexPrecios() {
  const { user } = useAuth();
  const { data, isLoading } = usePemex();
  const pubs = data?.items || [];
  const vigentes = pubs.filter((p) => !p.sustituida_por);
  const ultima = [...vigentes].sort((a, b) => (b.fecha_publicacion || "").localeCompare(a.fecha_publicacion || "") || b.vigencia_inicio.localeCompare(a.vigencia_inicio))[0];
  const pubId = ultima?.id;
  const [q, setQ] = useState("");
  const pub = ultima;
  const { data: rows, isLoading: lr } = useQuery({
    queryKey: ["pemex", "precios", pubId],
    queryFn: async () => (await api.get(`/control-semanal/pemex/publicaciones/${pubId}/precios`)).data.items,
    enabled: !!pubId,
  });
  const shown = useMemo(() => (rows || []).filter((r) => !q || r.punto.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const canImport = !!user?.is_admin;
  if (isLoading) return <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>;
  return (
    <div className="space-y-4" data-testid="pemex-precios">
      {canImport ? <PemexActions fuente={data?.fuente} documento={data?.documento} /> : <div className={`${card} p-4`}><FuenteLinks fuente={data?.fuente} documento={data?.documento} /></div>}
      {!pubs.length ? (
        <div className={`${card} p-12 text-center`} data-testid="pemex-empty">
          <p className="text-slate-600 font-semibold">Aún no hay documentos de PEMEX importados.</p>
          <p className="text-sm text-slate-400 mt-1">Consulta la fuente oficial o importa el PDF «Precios de Comercialización de GLP».</p>
        </div>
      ) : <>
        <div className={`${card} p-4 flex flex-wrap items-center gap-3`}>
          <span className="text-sm font-semibold text-slate-800" data-testid="pemex-ultima">Última circular publicada · {pub && pubLabel(pub)}</span>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar punto de entrega" data-testid="pemex-search"
              className="h-9 w-64 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 focus:outline-none" />
          </div>
          {pub && <div className="ml-auto"><ExportExcelButton testid="pemex-export" fileName={`Precios_PEMEX_${folio(pub.circular)}`} titulo={pub.titulo}
            hojas={[{ name: "Precios PEMEX", selector: '[data-testid="pemex-precios-table"]' }]} /></div>}
        </div>
        {pub && (
          <div className={`${card} flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 px-4 py-3`} data-testid="pemex-pub-info">
            <span className="font-semibold text-slate-800 inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-red-500" />{pub.titulo}</span>
            <span>Circular: <b>{pub.circular}</b></span>
            <span>Publicación: <b>{pub.fecha_publicacion ? fmtD(pub.fecha_publicacion) : "—"}</b></span>
            <span>Vigencia: <b>{fmtD(pub.vigencia_inicio)} – {fmtD(pub.vigencia_fin)}</b></span>
            {pub.sustituida_por && <span className="font-semibold text-amber-700" data-testid="pemex-cancelada">Cancelada y sustituida por {pub.sustituida_por}</span>}
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Cargada {fmtDT(pub.imported_at)} por {pub.imported_by_name || "—"}</span>
          </div>
        )}
        <TablaPrecios rows={shown} loading={lr} />
      </>}
    </div>
  );
}
